import {
  PageShell,
  Heading,
  SectionLabel,
  Card,
  CardHeader,
  RowList,
  Row,
  Badge,
  Stat,
  EmptyState,
  Divider,
} from '../../features/design-language/designLanguagePrimitives';
import { CompassSpinner } from '../../components/ui/CompassSpinner';
import { WeatherStatusBadge } from '../../components/ui/WeatherStatusBadge';
import { AltitudeIndicator } from '../../components/ui/AltitudeIndicator';
import { Button } from '../../components/ui/Button';
import { Input, FormField } from '../../components/ui/Input';

/**
 * The catalog's entries.
 *
 * The data model lives in ./componentCatalog.types; the ds/ family's entries
 * live in ./componentCatalog.ds and are appended below. Split that way so a
 * family can be documented without touching every other family's file.
 */
import { DS_CATALOG } from './componentCatalog.ds';
import { GAP_CATALOG } from './componentCatalog.gaps';
import { RECORD_CATALOG } from './componentCatalog.records';
import type { CatalogEntry } from './componentCatalog.types';

/** Entries that predate the ds/ layer — elevation and ui/ primitives. */
const LEGACY_CATALOG: CatalogEntry[] = [

  // ── Atoms ─────────────────────────────────────────────────────────
  {
    id: 'badge',
    name: 'Badge',
    tier: 'atom',
    status: 'live',
    importPath: '@/components/elevation',
    description: 'Inline mono badge. Six variants for status/among-row labeling.',
    render: () => (
      <div className="flex flex-wrap gap-2">
        <Badge>default</Badge>
        <Badge variant="outline">outline</Badge>
        <Badge variant="green">green</Badge>
        <Badge variant="amber">amber</Badge>
        <Badge variant="red">red</Badge>
        <Badge variant="blue">blue</Badge>
      </div>
    ),
  },
  {
    id: 'heading',
    name: 'Heading',
    tier: 'atom',
    status: 'live',
    importPath: '@/components/elevation',
    description: 'Semantic heading (fieldwork). Sizes xl / lg / md / sm; polymorphic h1–h4.',
    render: () => (
      <div className="space-y-2">
        <Heading size="xl">Heading xl</Heading>
        <Heading size="lg">Heading lg</Heading>
        <Heading size="md">Heading md</Heading>
        <Heading size="sm">Heading sm</Heading>
      </div>
    ),
  },
  {
    id: 'section-label',
    name: 'SectionLabel',
    tier: 'atom',
    status: 'live',
    importPath: '@/components/elevation',
    description: 'Uppercase mono eyebrow above a section heading.',
    render: () => <SectionLabel>Infrastructure</SectionLabel>,
  },
  {
    id: 'stat',
    name: 'Stat',
    tier: 'atom',
    status: 'live',
    importPath: '@/components/elevation',
    description: 'Label + large value pair for stat grids.',
    render: () => (
      <div className="flex gap-8">
        <Stat label="Total instances" value={42} />
        <Stat label="Active" value={8} />
      </div>
    ),
  },
  {
    id: 'divider',
    name: 'Divider',
    tier: 'atom',
    status: 'live',
    importPath: '@/components/elevation',
    description: 'Hairline horizontal rule on the charcoal ramp.',
    render: () => (
      <div className="w-full">
        <p className="text-sm text-[var(--text-sec)]">Above</p>
        <Divider />
        <p className="text-sm text-[var(--text-sec)]">Below</p>
      </div>
    ),
  },
  {
    id: 'compass-spinner',
    name: 'CompassSpinner',
    tier: 'atom',
    status: 'live',
    importPath: '@/components/ui/CompassSpinner',
    description: 'Brand loading indicator — a compass needle that settles to true north.',
    render: () => (
      <div className="flex items-center gap-4">
        <CompassSpinner size={32} />
        <CompassSpinner size={48} />
      </div>
    ),
  },
  {
    id: 'weather-status-badge',
    name: 'WeatherStatusBadge',
    tier: 'atom',
    status: 'live',
    importPath: '@/components/ui/WeatherStatusBadge',
    description: 'Project/cycle health as weather metaphor. status: on_track | at_risk | blocked | planning | complete | paused.',
    render: () => (
      <div className="flex flex-wrap gap-2">
        <WeatherStatusBadge status="on_track" />
        <WeatherStatusBadge status="at_risk" />
        <WeatherStatusBadge status="blocked" />
        <WeatherStatusBadge status="complete" />
      </div>
    ),
  },
  {
    id: 'altitude-indicator',
    name: 'AltitudeIndicator',
    tier: 'atom',
    status: 'live',
    importPath: '@/components/ui/AltitudeIndicator',
    description: 'Item altitude (1–5) as rising terrain bars — governs rank comparability.',
    render: () => (
      <div className="flex flex-wrap items-center gap-4">
        <AltitudeIndicator level={1} />
        <AltitudeIndicator level={3} />
        <AltitudeIndicator level={5} />
      </div>
    ),
  },
  {
    id: 'priorities-logo',
    name: 'PrioritiesLogo',
    tier: 'atom',
    status: 'documented',
    importPath: '@/components/ui/PrioritiesLogo',
    description: 'Brand wordmark/mark lockup. Props: markOnly, height.',
    note: 'Reads the GLOBAL theme via useTheme() rather than a local prop, so it would not follow a per-specimen preview — documented instead of live. See Brand → Logo System for on-plate lockups.',
  },
  {
    id: 'plate-stack',
    name: 'PlateStack',
    tier: 'atom',
    status: 'documented',
    importPath: '@/components/PlateStack',
    description: 'Brand spot-art — layered plates, richly variant-driven with hover/scroll animation.',
    note: 'Variant-driven with entrance animation; rendered live on the legacy /component-library "Visual Patterns" tab. Catalogued here pending a curated variant matrix.',
  },
  {
    id: 'button-atom',
    name: 'Button',
    tier: 'atom',
    status: 'live',
    importPath: '@/components/ui/Button',
    description:
      'Shared button, codified from the dominant inline patterns. variant: primary (sage) | cta (indigo, modal CTAs) | secondary | ghost | danger; size: sm | md. Dark mode via index.css auto-remap.',
    render: () => (
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="primary">Primary</Button>
        <Button variant="cta">CTA</Button>
        <Button variant="secondary">Secondary</Button>
        <Button variant="ghost">Ghost</Button>
        <Button variant="danger">Danger</Button>
        <Button variant="primary" size="sm">Small</Button>
        <Button variant="primary" disabled>Disabled</Button>
      </div>
    ),
  },
  {
    id: 'input-atom',
    name: 'Input / FormField',
    tier: 'atom',
    status: 'live',
    importPath: '@/components/ui/Input',
    description:
      'Shared text input, textarea, and labeled FormField wrapper (label + hint/error). Indigo focus lineage per the app-design skill; dark mode via the bare-input styling in index.css.',
    render: () => (
      <div className="flex w-full max-w-sm flex-col gap-4">
        <FormField label="Workspace name" hint="Shown to facilitators and participants.">
          <Input placeholder="Northwind Q3 planning" />
        </FormField>
        <FormField label="Email" error="That address is already in use.">
          <Input type="email" defaultValue="kaleo@kaleo" />
        </FormField>
      </div>
    ),
  },

  // ── Molecules ─────────────────────────────────────────────────────
  {
    id: 'card',
    name: 'Card',
    tier: 'molecule',
    status: 'live',
    importPath: '@/components/elevation',
    description: 'Bordered rounded-xl surface, no shadow. flush removes padding for row lists.',
    render: () => (
      <Card className="w-full max-w-sm">
        <Heading size="md">Card title</Heading>
        <p className="text-sm text-charcoal-600 dark:text-charcoal-400 mt-1">
          Body copy inside a standard content card.
        </p>
      </Card>
    ),
  },
  {
    id: 'card-header',
    name: 'CardHeader',
    tier: 'molecule',
    status: 'live',
    importPath: '@/components/elevation',
    description: 'Title + optional label/subtitle/count/action, for the top of a flush Card.',
    render: () => (
      <Card flush className="w-full max-w-sm">
        <CardHeader label="Active" title="Core engines" subtitle="Runtime services" count={8} />
      </Card>
    ),
  },
  {
    id: 'empty-state',
    name: 'EmptyState',
    tier: 'molecule',
    status: 'live',
    importPath: '@/components/elevation',
    description: 'Centered placeholder with title, optional description and action.',
    render: () => (
      <Card className="w-full max-w-sm">
        <EmptyState title="No items yet" description="Create your first item to get started." />
      </Card>
    ),
  },
  {
    id: 'app-select',
    name: 'AppSelect',
    tier: 'molecule',
    status: 'documented',
    importPath: '@/components/ui/AppSelect',
    description: 'Styled select control used across app forms.',
    note: 'Renderable, but its value semantics are form-bound; catalogued here — promote to live once a standalone demo value set is curated.',
  },
  {
    id: 'metric-card',
    name: 'MetricCard',
    tier: 'molecule',
    status: 'documented',
    importPath: '@/components/analytics/MetricCard',
    description: 'Analytics metric tile (value + label + delta).',
    note: 'Lives in a feature folder (analytics); reusable but coupled to analytics props. Candidate to generalize into the design system.',
  },
  {
    id: 'breadcrumb',
    name: 'Breadcrumb / DualBreadcrumb',
    tier: 'molecule',
    status: 'documented',
    importPath: '@/components/Breadcrumb',
    description: 'Wayfinding breadcrumb trail.',
    note: 'Requires react-router context (uses Link). Renders inside the app router but not in an isolated specimen frame.',
  },

  // ── Organisms ─────────────────────────────────────────────────────
  {
    id: 'rowlist-card',
    name: 'RowList in Card',
    tier: 'organism',
    status: 'live',
    importPath: '@/components/elevation',
    description: 'The canonical list pattern: flush Card + CardHeader + RowList of Rows.',
    render: () => (
      <Card flush className="w-full max-w-sm">
        <CardHeader label="Active" title="Engines" count={3} />
        <RowList>
          <Row onClick={() => {}}>
            <span className="flex-1">Prospector</span>
            <Badge variant="green">gathering</Badge>
          </Row>
          <Row onClick={() => {}}>
            <span className="flex-1">Synthesizer</span>
            <Badge variant="amber">idle</Badge>
          </Row>
          <Row onClick={() => {}}>
            <span className="flex-1">Publisher</span>
            <Badge variant="blue">ready</Badge>
          </Row>
        </RowList>
      </Card>
    ),
  },
  {
    id: 'panels',
    name: 'ModalPanel / SlideOverPanel',
    tier: 'organism',
    status: 'documented',
    importPath: '@/components/panels',
    description: 'App overlay system — modals, slide-overs, minimizable panels.',
    note: 'Requires PanelProvider + usePanels hooks. Not renderable in an isolated frame; drive it from a page wrapped in the provider.',
  },
  {
    id: 'toast',
    name: 'ToastProvider',
    tier: 'organism',
    status: 'documented',
    importPath: '@/components/ui/Toast',
    description: 'Context-based toast notification system.',
    note: 'Requires ToastProvider at the app root; toasts are triggered via a hook, so there is nothing to render standalone.',
  },
  {
    id: 'collection-grid',
    name: 'Collection grid (EntityTable, FilterBar…)',
    tier: 'organism',
    status: 'documented',
    importPath: '@/components/collection',
    description: 'Data-grid subsystem — tables, filters, column pickers, selection.',
    note: 'Requires CollectionContext + data. Feature-scale organism; catalogued for completeness, not rendered here.',
  },

  // ── Templates ─────────────────────────────────────────────────────
  {
    id: 'page-shell',
    name: 'PageShell',
    tier: 'template',
    status: 'live',
    importPath: '@/components/elevation',
    description: 'Outermost Elevation page scaffold — max width, padding, body font, text color.',
    render: () => (
      <div className="w-full rounded-lg border border-dashed border-[var(--rule-bright)]">
        <PageShell className="!py-4">
          <SectionLabel>Category</SectionLabel>
          <Heading as="h1" size="lg">Page inside a PageShell</Heading>
          <p className="text-sm text-charcoal-600 dark:text-charcoal-400 mt-1">
            The shell sets width, padding, and typography for every Elevation page.
          </p>
        </PageShell>
      </div>
    ),
  },
];

/**
 * ds/ first: it is the layer new work should reach for, and a catalog that
 * buries its current vocabulary under its legacy one teaches the wrong lesson.
 */
export const CATALOG: CatalogEntry[] = [...DS_CATALOG, ...RECORD_CATALOG, ...LEGACY_CATALOG, ...GAP_CATALOG];
